import { cabeNoAlcance } from './alcance';
import type { CaminhoConstruido } from './sprint';

/**
 * A LICENÇA DE LEITURA DE CADA TELA — a fonte única de "quem abre esta tela".
 *
 * Achado dos robôs de uso real no QAS (20/10/2026): o menu da mesa oferecia
 * telas que recusavam o perfil inteiro ("Você não tem acesso a esta tela") —
 * a Visão do gestor ao operador, Compras à oficina, Perfis e responsáveis e
 * Auditoria a quem não lê a governança nem a trilha, Receita ao gestor
 * operacional, Inspeção à controladoria — e escondia a inspeção do operador,
 * que a abre pelo endereço. A causa era ter DUAS listas: a rota guardava a
 * tela por `comLeitura` com a licença escrita ali, e o menu decidia pelo
 * módulo (uma licença por módulo, `PERMISSAO_QUE_ABRE`), sem olhar a tela.
 *
 * Agora as duas pontas leem DAQUI: a rota (`src/rotas/rotas.tsx`, pelo
 * `comLeitura`), a seção do menu (`secoesQueAbre`) e o módulo do menu (aparece
 * quando ao menos uma tela dele abre). O celular segue o mesmo recorte
 * (`TelaMais`); o teste `src/componentes/menu-segue-a-tela.test.ts` cobra a
 * paridade das três.
 *
 * O valor é a licença que a tela pede para LER (o `config.exige` da primeira
 * rota que ela consulta, no backend):
 *  - uma string: a licença;
 *  - uma lista: basta uma delas (Pessoas abre para quem lê impedimento OU
 *    documento de pessoa, e mostra o que cada um lê);
 *  - `null`: só a sessão (o Painel; cada bloco dele obedece ao módulo de
 *    origem).
 *
 * O `Record` cobra exaustividade: caminho novo em `CaminhoConstruido` sem a
 * decisão daqui é erro de compilação, e não uma tela que o menu oferece a
 * qualquer um.
 */
export const LEITURA_DA_TELA = {
  '/equipamentos': 'equipamento.ler',
  '/equipamentos/novo': 'equipamento.ler',
  '/equipamentos/catalogo': 'catalogo.ler',
  /* Decisão 64: o arquivo dos adesivos lê a frota, e só ela. */
  '/equipamentos/identificacao': 'equipamento.ler',
  '/equipamentos/renovacao': 'renovacao.ler',
  '/combustivel': 'combustivel.ler',
  '/combustivel/lancamento': 'combustivel.ler',
  '/combustivel/lancamento/completo': 'combustivel.ler',
  '/combustivel/tanques': 'combustivel.ler',
  '/combustivel/ordens': 'combustivel.ler',
  '/combustivel/consumo': 'combustivel.ler',
  '/combustivel/cobrancas': 'combustivel.ler',
  '/combustivel/folhas': 'combustivel.ler',
  '/combustivel/cadastros': 'combustivel.ler',
  '/operacao': 'operacao.ler',
  '/operacao/cadastros': 'operacao.ler',
  '/operacao/folhas': 'operacao.ler',
  '/operacao/conciliacao': 'operacao.ler',
  '/operacao/importacoes': 'operacao.ler',
  '/operacao/telemetria': 'telemetria.ler',
  /* As segundas telas dos submodulos leem a licenca do submodulo, a mesma que
     a rota delas ja usa (`TELA_DA_TELEMETRIA`, `TELA_DA_PREDITIVA`,
     `TELA_DAS_INTEGRACOES` em `src/rotas/rotas.tsx`). */
  '/operacao/telemetria/fila': 'telemetria.ler',
  '/operacao/telemetria/conciliacao': 'telemetria.ler',
  '/operacao/telemetria/integracoes': 'telemetria.ler',
  '/manutencao': 'manutencao.ler',
  '/manutencao/preditiva': 'preditiva.ler',
  '/manutencao/preditiva/recorrencias': 'preditiva.ler',
  '/manutencao/preditiva/previsoes': 'preditiva.ler',
  '/manutencao/preditiva/propostas': 'preditiva.ler',
  '/ordens': 'os.ler',
  '/inspecao': 'inspecao.ler',
  '/inspecao/checklists': 'inspecao.ler',
  '/pessoas': ['impedimento.ler', 'documento_de_pessoa.ler'],
  '/estoque': 'estoque.ler',
  '/compras': 'compras.ler',
  '/painel': null,
  '/painel/gestor': 'custo.ler',
  '/painel/ia': 'ia.auditar',
  '/painel/piloto': 'piloto.ler',
  '/financeiro': 'financeiro.ler',
  '/orcamento': 'orcamento.ler',
  '/investimentos': 'renovacao.ler',
  '/custos': 'custo.ler',
  '/receita': 'receita.ler',
  '/tecnico': 'documento_tecnico.ler',
  '/cadastros': 'cadastro.ler',
  '/cadastros/governanca': 'governanca.ler',
  '/cadastros/trilha': 'auditoria.ler',
  '/cadastros/integracoes': 'integracao.ler',
  '/cadastros/integracoes/lotes': 'integracao.ler',
  '/cadastros/integracoes/titulos': 'integracao.ler',
  '/cadastros/integracoes/correspondencias': 'integracao.ler',
  '/cadastros/integracoes/divergencias': 'integracao.ler',
} as const satisfies Record<CaminhoConstruido, string | readonly string[] | null>;

/** As licenças que abrem a tela (basta uma); lista vazia é "só a sessão". */
export function licencasDaTela(caminho: CaminhoConstruido): readonly string[] {
  const leitura: string | readonly string[] | null = LEITURA_DA_TELA[caminho];
  if (leitura === null) return [];
  return typeof leitura === 'string' ? [leitura] : leitura;
}

/**
 * A pessoa abre a tela? `null` é a sessão em trânsito: a tela de CONSULTA
 * aparece (o menu não pisca vazio no primeiro quadro); quem decide a ação é o
 * `exige` da seção, que com a sessão em trânsito não aparece.
 */
export function abreATela(
  caminho: CaminhoConstruido,
  permissoes: readonly string[] | null,
  /* O alcance da sessão (rodada 3 dos robôs): quem tem só parte da empresa
     não abre a tela da empresa inteira (`./alcance`). */
  alcanceParcial = false,
): boolean {
  if (!cabeNoAlcance(caminho, alcanceParcial)) return false;
  if (permissoes === null) return true;
  const licencas = licencasDaTela(caminho);
  return licencas.length === 0 || licencas.some((licenca) => permissoes.includes(licenca));
}
